import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Stack, Typography } from "@mui/material";
import OutsideClickHandler from "react-outside-click-handler";
import ProfileMenu from "../../../components/ProfileMenu";
import { INK, SectionLabel, TextAction } from "../../../components/Ink";
import useArtistProfileActions from "../hooks/useArtistProfileActions";
import { playlistArt, playlistFacts, songArt, songFacts } from "./highlights";

const SHARE_KEYS = ["m14", "m15", "m16"];

// "Share" with the copy / Facebook / X menu under it.
function ShareAction({ onPick }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  return (
    <Box sx={{ position: "relative" }}>
      <TextAction onClick={() => setOpen((v) => !v)} aria-expanded={open}>Share</TextAction>
      {open ? (
        <OutsideClickHandler onOutsideClick={() => setOpen(false)}>
          <ProfileMenu
            right="" isHeader isLast="24px !important"
            actions={SHARE_KEYS.map((key) => ({ key, action: t(`dropMenu.${key}`) }))}
            handleProfileMenuChoice={(action) => {
              onPick(action);
              setOpen(false);
            }}
          />
        </OutsideClickHandler>
      ) : null}
    </Box>
  );
}

// One flat card: section label, art, title, a muted facts line, text actions.
// Clicking anywhere else on the card plays the item.
function HighlightCard({ label, art, title, facts, actions, onOpen, sx }) {
  return (
    <Box className="ap-highlight-card" onClick={onOpen} sx={{ ...sx, cursor: "pointer", minWidth: 0, "&:hover .ink-primary": { color: INK.accent } }}>
      <SectionLabel sx={{ mt: 0 }}>{label}</SectionLabel>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, minWidth: 0 }}>
        <Box component="img" src={art} alt="" sx={{ width: 64, height: 64, borderRadius: "6px", objectFit: "cover", flexShrink: 0, display: "block", bgcolor: "rgba(255,255,255,0.06)" }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography className="ink-primary" title={title} sx={{ fontSize: 15, fontWeight: 700, color: INK.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 12, color: INK.muted, mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{facts}</Typography>
          <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
            {actions}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

// The profile's Top song and Top playlist, two panels in the `.ap-panels` grid.
export default function TrendingSection({ trendingMusic, onPlayMusic, onPlayPlaylist, onAddToPlaylist, sx }) {
  const history = useHistory();
  const actions = useArtistProfileActions();
  const song = trendingMusic?.topMusic;
  const playlist = trendingMusic?.topPlaylist;
  if (!song?._id && !playlist?._id) return null;

  return (
    <Box className="ap-highlights-row">
      {song?._id ? (
        <HighlightCard
          label="Top song" art={songArt(song)} title={song.title} facts={songFacts(song)} sx={sx}
          onOpen={() => onPlayMusic(song)}
          actions={
            <>
              <TextAction onClick={() => onPlayMusic(song)}>Play</TextAction>
              <TextAction onClick={() => onAddToPlaylist(song._id)}>Add to playlist</TextAction>
              <ShareAction onPick={(action) => actions.handleCopy(action, false, trendingMusic)} />
            </>
          }
        />
      ) : null}
      {playlist?._id ? (
        <HighlightCard
          label="Top playlist" art={playlistArt(playlist)} title={playlist.title} facts={playlistFacts(playlist)} sx={sx}
          onOpen={() => onPlayPlaylist(playlist)}
          actions={
            <>
              <TextAction onClick={() => onPlayPlaylist(playlist)}>Play</TextAction>
              <TextAction onClick={() => history.push(`/app/PlaylistStatics/${playlist._id}/${playlist.owner}`)}>Stats</TextAction>
              <ShareAction onPick={(action) => actions.handleCopy(action, true, trendingMusic)} />
            </>
          }
        />
      ) : null}
    </Box>
  );
}
